import type { Portfolio } from 'venn-api';

export type Path = number[];

// Path <-> key helpers
const PATH_SEPARATOR = ',';
export const toPathKey = (path: Path): string => path.join(PATH_SEPARATOR);
export const fromPathKey = (key: string): Path =>
  key.length ? key.split(PATH_SEPARATOR).map((n) => parseInt(n, 10)) : [];

export const isExcluded = (path: Path, excluded: Set<string>): boolean => excluded.has(toPathKey(path));

// Normalize grid path: strip a leading root (0) only when there is more than one segment
export const normalizeGridPath = (path: Path = []): Path => (path.length > 1 && path[0] === 0 ? path.slice(1) : path);

const findNode = (
  root: Portfolio,
  predicate: (node: Portfolio) => boolean,
  startPath: Path = [],
): { node: Portfolio; path: Path } | null => {
  if (predicate(root)) {
    return { node: root, path: startPath };
  }
  for (let i = 0; i < (root.children?.length ?? 0); i++) {
    const child = root.children[i]!;
    const found = findNode(child, predicate, [...startPath, i]);
    if (found) return found;
  }
  return null;
};

// Path-based helpers
export const getAtPath = (root: Portfolio, path: Path): Portfolio | undefined => {
  let node: Portfolio | undefined = root;
  for (const idx of path) {
    if (!node?.children || idx < 0 || idx >= node.children.length) return undefined;
    node = node.children[idx];
  }
  return node;
};

export const updateAtPath = (root: Portfolio, path: Path, updater: (node: Portfolio) => Portfolio): Portfolio => {
  if (path.length === 0) return updater(root);
  const [head, ...tail] = path;
  const children = root.children ?? [];
  return {
    ...root,
    children: children.map((child, idx) => (idx === head ? updateAtPath(child, tail, updater) : child)),
  };
};

export const removeAtPath = (root: Portfolio, path: Path): Portfolio => {
  if (path.length === 0) return root; // cannot remove root
  const parentPath = path.slice(0, -1);
  const removeIdx = path[path.length - 1]!;
  return updateAtPath(root, parentPath, (parent) => ({
    ...parent,
    children: (parent.children ?? []).filter((_, i) => i !== removeIdx),
  }));
};

// Fund utilities
export function* iterateFunds(root: Portfolio, startPath: Path = []): Generator<{ node: Portfolio; path: Path }> {
  if (root.fund) {
    yield { node: root, path: startPath };
    return;
  }
  for (let i = 0; i < (root.children?.length ?? 0); i++) {
    const child = root.children[i]!;
    yield* iterateFunds(child, [...startPath, i]);
  }
}

export const listFundIds = (root: Portfolio): string[] => {
  const out = new Set<string>();
  for (const { node } of iterateFunds(root)) {
    const id = node.fund?.id;
    if (id) out.add(id);
  }
  return Array.from(out);
};

// High-level helpers
export const findFundById = (root: Portfolio, fundId: string): { node: Portfolio; path: Path } | null => {
  return findNode(root, (node) => node.fund?.id === fundId);
};

export const appendChildAtPath = (root: Portfolio, parentPath: Path, child: Portfolio): Portfolio => {
  return updateAtPath(root, parentPath, (parent) => ({
    ...parent,
    children: [...(parent.children ?? []), child],
  }));
};

export const prependChildAtRoot = (root: Portfolio, child: Portfolio): Portfolio =>
  updateAtPath(root, [], (parent) => ({ ...parent, children: [child, ...(parent.children ?? [])] }));

/**
 * Ensure a chain of category/strategy nodes exists under root with provided names.
 * Returns the updated root and the path to the deepest node.
 */
export const ensureCategoryPath = (
  root: Portfolio,
  names: string[],
  makeNode: (name: string, parent: Portfolio) => Portfolio,
): { root: Portfolio; path: Path } => {
  let current = root;
  let currentPath: Path = [];
  let updatedRoot = root;

  names.forEach((name, _depth) => {
    const existingIdx =
      current.children?.findIndex((c) => !c.fund && c.designation !== 'CASH' && c.name === name) ?? -1;
    if (existingIdx >= 0) {
      currentPath = [...currentPath, existingIdx];
      current = (current.children ?? [])[existingIdx]!;
      return;
    }

    // Need to create the node at the current level
    const newNode = makeNode(name, current);
    updatedRoot = appendChildAtPath(updatedRoot, currentPath, newNode);

    // After append, the new node is at the end of children
    const newIdx = current.children?.length ?? 0;
    currentPath = [...currentPath, newIdx];
    // Re-fetch current via path to keep references in sync
    const maybe = getAtPath(updatedRoot, currentPath);
    current = maybe ?? newNode;
  });

  return { root: updatedRoot, path: currentPath };
};

/**
 * After mutating a portfolio tree, realign the set of excluded path keys (comma-separated indices)
 * from the previous tree to the next tree. This ensures that excluded paths remain valid
 * even if the tree structure has changed.
 */
export const remapExcludedPathKeys = (
  prevRoot: Portfolio,
  nextRoot: Portfolio,
  prevExcluded: Set<string>,
): Set<string> => {
  const next = new Set<string>();
  for (const key of prevExcluded) {
    try {
      const oldPath = fromPathKey(key);
      const normalizedOldPath = normalizeGridPath(oldPath);
      const prevNode = getAtPath(prevRoot, normalizedOldPath);
      if (!prevNode) {
        next.add(key);
        continue;
      }

      const fundId = prevNode.fund?.id;
      let newPath: Path | undefined;

      if (fundId) {
        const located = findNode(nextRoot, (node) => node.fund?.id === fundId);
        newPath = located?.path;
      } else {
        // Fallback for unmapped funds: try to locate by reference or name
        const byRef = findNode(nextRoot, (node) => node === prevNode);
        if (byRef) {
          newPath = byRef.path;
        } else if (prevNode.name) {
          const byName = findNode(nextRoot, (node) => !!node.fund && !node.fund.id && node.name === prevNode.name);
          newPath = byName?.path;
        }
      }

      if (newPath) {
        next.add(toPathKey([0, ...newPath]));
      } else {
        next.add(key);
      }
    } catch {
      next.add(key);
    }
  }
  return next;
};
